import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Info, FileText, Layers } from "lucide-react";

function GroupEditor({ open, onClose, initial, parks, onSaved }) {
  const isNew = !initial?.id;
  const [name, setName] = useState("");
  const [order, setOrder] = useState(0);
  const [active, setActive] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setOrder(initial?.order ?? 0);
      setActive(initial?.active ?? true);
      setSelectedIds(initial?.park_ids || []);
    }
  }, [open, initial]);

  const togglePark = (pid) => {
    setSelectedIds((cur) => (cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid]));
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Name is required");
    if (selectedIds.length === 0) return toast.error("Assign at least one park");
    setSaving(true);
    try {
      const payload = { name: name.trim(), order: Number(order) || 0, active, park_ids: selectedIds };
      if (isNew) await api.post("/admin/report-groups", payload);
      else await api.put(`/admin/report-groups/${initial.id}`, payload);
      toast.success(isNew ? "Group created" : "Group updated");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{isNew ? "New Report Group" : "Edit Report Group"}</DialogTitle>
          <DialogDescription>
            Each group generates a single monthly DOCX containing one section per assigned park.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_120px_auto] md:items-end">
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Group name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="rg-form-name" />
            </div>
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Order</Label>
              <Input type="number" value={order} onChange={(e) => setOrder(e.target.value)} data-testid="rg-form-order" />
            </div>
            <div className="flex items-center gap-2 pb-1">
              <Switch checked={active} onCheckedChange={setActive} id="rg-active" data-testid="rg-form-active" />
              <Label htmlFor="rg-active" className="text-xs">Active</Label>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-xs">Parks in this group ({selectedIds.length} selected)</Label>
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setSelectedIds(parks.map((p) => p.id))} data-testid="rg-form-select-all">Select all</Button>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setSelectedIds([])} data-testid="rg-form-clear">Clear</Button>
              </div>
            </div>
            <div className="max-h-[320px] overflow-y-auto rounded-md border bg-slate-50 p-2">
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {parks.map((p) => {
                  const checked = selectedIds.includes(p.id);
                  return (
                    <label
                      key={p.id}
                      className={`flex cursor-pointer items-center gap-2 rounded border bg-white px-2 py-1.5 text-[13px] transition-colors hover:bg-amber-50 ${checked ? "border-indigo-300 bg-indigo-50/40" : ""}`}
                      data-testid={`rg-form-park-${p.code}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => togglePark(p.id)} />
                      <span className="font-mono text-[11px] text-slate-500">{p.code}</span>
                      <span className="truncate">{p.name}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="rg-form-save">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ReportGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [parks, setParks] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const parksById = useMemo(() => Object.fromEntries(parks.map((p) => [p.id, p])), [parks]);

  const reload = async () => {
    const [g, p] = await Promise.all([
      api.get("/admin/report-groups").then((r) => r.data),
      api.get("/parks").then((r) => r.data),
    ]);
    setGroups(g);
    setParks(p);
  };

  useEffect(() => { reload().catch(() => toast.error("Failed to load")); }, []);

  const remove = async (g) => {
    if (!window.confirm(`Delete report group "${g.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/report-groups/${g.id}`);
      toast.success("Group deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <AppShell
      title="Report Groups"
      subtitle="Define which parks belong to each monthly DOCX report"
      action={
        <Button onClick={() => { setEditing(null); setEditorOpen(true); }} data-testid="rg-new-btn">
          <Plus className="mr-1.5 h-4 w-4" /> New group
        </Button>
      }
    >
      <div className="space-y-4">
        <Alert className="border-sky-200 bg-sky-50/60">
          <Info className="h-4 w-4 text-sky-700" />
          <AlertTitle className="text-sky-900">About report groups</AlertTitle>
          <AlertDescription className="text-sky-900/90 text-[13px] leading-relaxed">
            By default we ship 3 groups matching ITCOT's existing report bundling. You can edit a group's parks,
            create new groups (e.g., a region-specific one), or remove the ones you don't need. Changes apply
            immediately to the next download from <span className="font-medium">Monthly Reports</span>.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {groups.map((g) => {
            const parkList = (g.park_ids || []).map((id) => parksById[id]).filter(Boolean);
            return (
              <Card key={g.id} data-testid={`rg-card-${g.id}`} className="flex flex-col">
                <CardHeader className="pb-2">
                  <div className="flex items-start gap-2">
                    <div className="grid h-8 w-8 place-items-center rounded-md bg-indigo-100 text-indigo-700">
                      <Layers className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <CardTitle className="text-[14px] leading-snug line-clamp-2">{g.name}</CardTitle>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span>Order {g.order}</span>
                        <span>·</span>
                        <span>{parkList.length} park{parkList.length !== 1 ? "s" : ""}</span>
                        {!g.active && <Badge variant="outline" className="text-rose-700 border-rose-300">inactive</Badge>}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col gap-3">
                  <div className="flex flex-wrap gap-1">
                    {parkList.map((p) => (
                      <Badge key={p.id} variant="outline" className="text-[10px] font-normal bg-slate-50">
                        <span className="font-mono mr-1 text-slate-500">{p.code}</span>{p.name}
                      </Badge>
                    ))}
                  </div>
                  <div className="mt-auto flex gap-1.5">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEditing(g); setEditorOpen(true); }} data-testid={`rg-edit-${g.id}`}>
                      <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => remove(g)} data-testid={`rg-delete-${g.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <GroupEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        initial={editing}
        parks={parks}
        onSaved={reload}
      />
    </AppShell>
  );
}
